import type { ReadonlyRecord } from '../../../../utils'

import type { PageSubscriptionsTypes, SingleSubscriptionsTypes } from '../../../../application'

import type { BackendHttpMethod, BackendPath, RSTAdapter } from '../../../Connectware'
import type { HttpRequestArgs } from '../../Base'
import {
    AgentsStrategy,
    ConnectionsStrategy,
    CoreContainerStrategy,
    DetailedConnectionStrategy,
    DetailedCoreContainerStrategy,
    DetailedEndpointStrategy,
    DetailedMappingStrategy,
    DetailedServiceContainerStrategy,
    DetailedServiceStrategy,
    DetailedVolumeStrategy,
    DeviatedServicesStrategy,
    DeviationsStrategy,
    EndpointsStrategy,
    MappingsStrategy,
    NodesStrategy,
    ResourceDeviationsStrategy,
    ServersStrategy,
    ServerStrategy,
    ServiceContainersStrategy,
    ServicesDataResourcesStrategy,
    ServicesLinksStrategy,
    ServicesStrategy,
    VolumesStrategy,
} from './implementations'
import type { SubscriptionStrategy } from './Strategy'

type StrategyConstructor<P extends keyof PageSubscriptionsTypes, S extends keyof SingleSubscriptionsTypes> = new(
    request: <Path extends BackendPath, Method extends BackendHttpMethod<Path>, Return>(args: HttpRequestArgs<Path, Method, Return>) => Promise<Return>,
    rstAdapter: RSTAdapter
) => SubscriptionStrategy<P, S>

const strategyCreators: ReadonlyRecord<
    keyof PageSubscriptionsTypes | keyof SingleSubscriptionsTypes,
    StrategyConstructor<keyof PageSubscriptionsTypes, keyof SingleSubscriptionsTypes>
> = {
    services: ServicesStrategy,
    deviatedServices: DeviatedServicesStrategy,
    service: DetailedServiceStrategy,
    servicesLinks: ServicesLinksStrategy,
    serviceResources: ServicesDataResourcesStrategy,
    endpoints: EndpointsStrategy,
    endpoint: DetailedEndpointStrategy,
    serviceDeviations: DeviationsStrategy,
    resourceDeviation: ResourceDeviationsStrategy,
    volumes: VolumesStrategy,
    volume: DetailedVolumeStrategy,
    serviceContainers: ServiceContainersStrategy,
    serviceContainer: DetailedServiceContainerStrategy,
    coreContainers: CoreContainerStrategy,
    coreContainer: DetailedCoreContainerStrategy,
    connections: ConnectionsStrategy,
    connection: DetailedConnectionStrategy,
    mappings: MappingsStrategy,
    mapping: DetailedMappingStrategy,
    agents: AgentsStrategy,
    nodes: NodesStrategy,
    servers: ServersStrategy,
    server: ServerStrategy,
}

export const getSubscriptionStrategyFactory = <P extends keyof PageSubscriptionsTypes, S extends keyof SingleSubscriptionsTypes>(
    type: P | S
): StrategyConstructor<P, S> => strategyCreators[type] as (typeof strategyCreators)[typeof type] & StrategyConstructor<P, S>
